import { RefObject, useRef, useState } from 'react'
import Konva from 'konva'
import type { KonvaEventObject } from 'konva/lib/Node'
import { getContentPointer } from './getContentPointer'

export type SelectionRect = {
  x: number
  y: number
  width: number
  height: number
}

type Params = {
  stageRef: RefObject<Konva.Stage | null>
  nodeRefs: RefObject<Map<string, Konva.Node>>
  canEdit: boolean
  onSelectIds: (ids: string[]) => void
  canSelect?: (id: string) => boolean
}

export const useSelectionBox = ({
  stageRef,
  nodeRefs,
  canEdit,
  onSelectIds,
  canSelect,
}: Params) => {
  const [selectionRect, setSelectionRect] = useState<SelectionRect | null>(null)
  const originRef = useRef<{ x: number; y: number } | null>(null)

  const onMouseDown = (event: KonvaEventObject<MouseEvent>) => {
    if (!canEdit || event.target !== event.target.getStage()) return
    const pos = getContentPointer(stageRef.current)
    if (!pos) return
    originRef.current = pos
    setSelectionRect({ x: pos.x, y: pos.y, width: 0, height: 0 })
  }

  const onMouseMove = (_event: KonvaEventObject<MouseEvent>) => {
    const origin = originRef.current
    if (!origin || !canEdit) return
    const pos = getContentPointer(stageRef.current)
    if (!pos) return
    setSelectionRect({
      x: Math.min(origin.x, pos.x),
      y: Math.min(origin.y, pos.y),
      width: Math.abs(pos.x - origin.x),
      height: Math.abs(pos.y - origin.y),
    })
  }

  const onMouseUp = () => {
    if (!selectionRect) return
    const rect = selectionRect
    originRef.current = null
    setSelectionRect(null)
    if (rect.width < 5 && rect.height < 5) return
    if (!nodeRefs.current) return
    const stage = stageRef.current
    if (!stage) return
    const matched: string[] = []
    nodeRefs.current.forEach((node, id) => {
      if (canSelect && !canSelect(id)) return
      const nodeRect = node.getClientRect({ relativeTo: stage })
      if (
        nodeRect.x < rect.x + rect.width &&
        nodeRect.x + nodeRect.width > rect.x &&
        nodeRect.y < rect.y + rect.height &&
        nodeRect.y + nodeRect.height > rect.y
      ) {
        matched.push(id)
      }
    })
    if (matched.length > 0) onSelectIds(matched)
  }

  return { selectionRect, onMouseDown, onMouseMove, onMouseUp }
}
